import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { requestFeedbackService } from "@/services/requestFeedbackService";
import { feedbackKeys } from "@/components/pages/feedbacks/hooks/feedbacks.hooks";
import {
  CreateRequestFeedbackDto,
  RequestFeedbackFilters,
  UpdateRequestFeedbackDto,
} from "@/types/requestFeedback";

export const requestFeedbackKeys = {
  all: ["request-feedback"] as const,
  lists: () => [...requestFeedbackKeys.all, "list"] as const,
  list: (filters?: RequestFeedbackFilters) =>
    [...requestFeedbackKeys.lists(), filters] as const,
  details: () => [...requestFeedbackKeys.all, "detail"] as const,
  detail: (id: string) => [...requestFeedbackKeys.details(), id] as const,
};

const DASHBOARD_QUERY_KEY = ["dashboard"] as const;

const invalidateRequestFeedbackRelatedQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: requestFeedbackKeys.all });
  queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
  queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
};

export const useRequestFeedback = (
  filters?: RequestFeedbackFilters,
  enabled = true,
) => {
  const isEnabled = enabled && !!filters?.requestId;
  return useQuery({
    queryKey: requestFeedbackKeys.list(filters),
    queryFn: () => requestFeedbackService.getRequestFeedback(filters!),
    enabled: isEnabled,
  });
};

export const useCreateRequestFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRequestFeedbackDto) =>
      requestFeedbackService.create(data),
    onSuccess: () => {
      invalidateRequestFeedbackRelatedQueries(queryClient);
    },
  });
};

export const useUpdateRequestFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateRequestFeedbackDto;
    }) => requestFeedbackService.update(id, data),
    onSuccess: (updated) => {
      queryClient.setQueryData(requestFeedbackKeys.detail(updated.id), updated);
      invalidateRequestFeedbackRelatedQueries(queryClient);
    },
  });
};

export const useDeleteRequestFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => requestFeedbackService.deleteFeedback(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({
        queryKey: requestFeedbackKeys.detail(deletedId),
      });
      invalidateRequestFeedbackRelatedQueries(queryClient);
    },
  });
};
